import { ColumnFiltersState, flexRender, type HeaderGroup, type Row } from "@tanstack/react-table";
import { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";

import { fontSizes } from "#app/theme/v1/fontSizes";
import { SortData } from "#modules/quoteRequestLines/fetchQuoteRequestLines.api";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { Flex } from "#shared/view/components/Flex";
import { Table } from "#shared/view/designSystem/Table";
import { QuoteRequestLinesListItem } from "#types/QuoteRequestLinesListItem";

type Props = {
  rows: Row<QuoteRequestLinesListItem>[];
  headerGroups: HeaderGroup<QuoteRequestLinesListItem>[];
  columnFilters: ColumnFiltersState;
  sort: SortData;
  setSort: Dispatch<SetStateAction<SortData>>;
};

export const QuoteRequestLinesTableView = ({ rows, headerGroups, sort, setSort }: Props) => {
  const navigate = useNavigate();

  const navigateToQuoteRequest = (id: string) => {
    navigate(ROUTES.partnerQuoteRequest(id));
  };

  const getSortingString = (header: string) => {
    const columnsMatching = {
      reference: "quoteRequest.reference",
      client: "quoteRequest.client.name",
      createdAt: "createdAt",
      quantity: "quantity",
      partName: "part.name",
    };

    const nextSortColumn = columnsMatching[header as keyof typeof columnsMatching] as
      | string
      | undefined;

    const currentSortColumn = sort?.split(":")[0];
    const currentSortOrder = sort?.split(":")[1];

    let sortingString;

    if (nextSortColumn === undefined) {
      sortingString = undefined;

      return;
    }

    if (currentSortColumn === nextSortColumn) {
      sortingString = `${currentSortColumn}:${currentSortOrder === "desc" ? "asc" : "desc"}`;
    } else {
      sortingString = `${nextSortColumn}:asc`;
    }

    return sortingString;
  };

  const sortOrder = (header: string) => getSortingString(header)?.split(":")[1];

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        {headerGroups.map((headerGroup) => (
          <Table.Tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <Table.Th
                key={header.id}
                onClick={() => setSort(getSortingString(header.column.id))}
                style={{
                  fontSize: fontSizes.medium,
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                <Flex gap="xxs" justify={"center"}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                  {sort?.includes(header.column.id) && sortOrder(header.column.id) === "desc" && (
                    <Icon name="chevronUp" size={20} />
                  )}
                  {sort?.includes(header.column.id) && sortOrder(header.column.id) === "asc" && (
                    <Icon name="chevronDown" size={20} />
                  )}
                </Flex>
              </Table.Th>
            ))}
          </Table.Tr>
        ))}
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr
            key={row.id}
            style={{
              cursor: "pointer",
            }}
            onClick={() => navigateToQuoteRequest(row.original.quoteRequest.id)}
          >
            {row.getVisibleCells().map((cell) => (
              <Table.Td
                key={cell.id}
                style={{
                  fontSize: fontSizes.medium,
                  textAlign: cell.column.id === "partName" ? "left" : "center",
                }}
                onClick={(e) => {
                  if (cell.column.id === "actions") {
                    e.stopPropagation();
                  }
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};
